import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';

// Importar traduções
import ptBR from './locales/pt-BR.json';
import enUS from './locales/en-US.json';
import esES from './locales/es-ES.json';
import frFR from './locales/fr-FR.json';
import deDE from './locales/de-DE.json';
import itIT from './locales/it-IT.json';
import zhCN from './locales/zh-CN.json';
import jaJP from './locales/ja-JP.json';
import koKR from './locales/ko-KR.json';
import ruRU from './locales/ru-RU.json';
import arSA from './locales/ar-SA.json';

// Configurações por país/região
export const localeConfigs = {
  'pt-BR': {
    name: 'Português (Brasil)',
    flag: '🇧🇷',
    currency: 'BRL',
    currencySymbol: 'R$',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: '.',
    },
    timezone: 'America/Sao_Paulo',
    rtl: false,
  },
  'pt-PT': {
    name: 'Português (Portugal)',
    flag: '🇵🇹',
    currency: 'EUR',
    currencySymbol: '€',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: ' ',
    },
    timezone: 'Europe/Lisbon',
    rtl: false,
  },
  'en-US': {
    name: 'English (United States)',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    dateFormat: 'MM/dd/yyyy',
    timeFormat: 'h:mm a',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'America/New_York',
    rtl: false,
  },
  'en-GB': {
    name: 'English (United Kingdom)',
    flag: '🇬🇧',
    currency: 'GBP',
    currencySymbol: '£',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'Europe/London',
    rtl: false,
  },
  'es-ES': {
    name: 'Español (España)',
    flag: '🇪🇸',
    currency: 'EUR',
    currencySymbol: '€',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: '.',
    },
    timezone: 'Europe/Madrid',
    rtl: false,
  },
  'es-MX': {
    name: 'Español (México)',
    flag: '🇲🇽',
    currency: 'MXN',
    currencySymbol: '$',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'America/Mexico_City',
    rtl: false,
  },
  'fr-FR': {
    name: 'Français (France)',
    flag: '🇫🇷',
    currency: 'EUR',
    currencySymbol: '€',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: ' ',
    },
    timezone: 'Europe/Paris',
    rtl: false,
  },
  'de-DE': {
    name: 'Deutsch (Deutschland)',
    flag: '🇩🇪',
    currency: 'EUR',
    currencySymbol: '€',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: '.',
    },
    timezone: 'Europe/Berlin',
    rtl: false,
  },
  'it-IT': {
    name: 'Italiano (Italia)',
    flag: '🇮🇹',
    currency: 'EUR',
    currencySymbol: '€',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: '.',
    },
    timezone: 'Europe/Rome',
    rtl: false,
  },
  'zh-CN': {
    name: '中文 (简体)',
    flag: '🇨🇳',
    currency: 'CNY',
    currencySymbol: '¥',
    dateFormat: 'yyyy/MM/dd',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'Asia/Shanghai',
    rtl: false,
  },
  'ja-JP': {
    name: '日本語',
    flag: '🇯🇵',
    currency: 'JPY',
    currencySymbol: '¥',
    dateFormat: 'yyyy/MM/dd',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'Asia/Tokyo',
    rtl: false,
  },
  'ko-KR': {
    name: '한국어',
    flag: '🇰🇷',
    currency: 'KRW',
    currencySymbol: '₩',
    dateFormat: 'yyyy. MM. dd.',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'Asia/Seoul',
    rtl: false,
  },
  'ru-RU': {
    name: 'Русский',
    flag: '🇷🇺',
    currency: 'RUB',
    currencySymbol: '₽',
    dateFormat: 'dd.MM.yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: ',',
      thousands: ' ',
    },
    timezone: 'Europe/Moscow',
    rtl: false,
  },
  'ar-SA': {
    name: 'العربية',
    flag: '🇸🇦',
    currency: 'SAR',
    currencySymbol: 'ر.س',
    dateFormat: 'dd/MM/yyyy',
    timeFormat: 'HH:mm',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
    timezone: 'Asia/Riyadh',
    rtl: true,
  },
};

// Detecção automática de localização
const detectLocale = async (): Promise<string> => {
  // Preferência do navegador primeiro (rápido, sem rede)
  const fromBrowser = (): string => {
    const browserLang = navigator.language || navigator.languages?.[0] || 'pt-BR';
    if (localeConfigs[browserLang as keyof typeof localeConfigs]) {
      return browserLang;
    }
    const langCode = browserLang.split('-')[0];
    const langMap: Record<string, string> = {
      pt: 'pt-BR',
      en: 'en-US',
      es: 'es-ES',
      fr: 'fr-FR',
      de: 'de-DE',
      it: 'it-IT',
      zh: 'zh-CN',
      ja: 'ja-JP',
      ko: 'ko-KR',
      ru: 'ru-RU',
      ar: 'ar-SA',
    };
    return langMap[langCode] || 'pt-BR';
  };

  try {
    // Timeout curto — se falhar (adblock/CORS/rede), usa o idioma do navegador
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2500);
    const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    window.clearTimeout(timer);
    if (!response.ok) return fromBrowser();

    const data = await response.json();
    const countryCode = data.country_code?.toLowerCase();
    const localeMap: Record<string, string> = {
      br: 'pt-BR',
      pt: 'pt-PT',
      us: 'en-US',
      gb: 'en-GB',
      ca: 'en-US',
      au: 'en-US',
      nz: 'en-US',
      es: 'es-ES',
      mx: 'es-MX',
      ar: 'es-ES',
      cl: 'es-ES',
      co: 'es-ES',
      pe: 'es-ES',
      fr: 'fr-FR',
      be: 'fr-FR',
      ch: 'fr-FR',
      de: 'de-DE',
      at: 'de-DE',
      it: 'it-IT',
      cn: 'zh-CN',
      hk: 'zh-CN',
      tw: 'zh-CN',
      jp: 'ja-JP',
      kr: 'ko-KR',
      ru: 'ru-RU',
      sa: 'ar-SA',
      ae: 'ar-SA',
      eg: 'ar-SA',
    };

    if (countryCode && localeMap[countryCode]) {
      return localeMap[countryCode];
    }
  } catch {
    // Falha esperada (rede, bloqueio de extensões, CORS). Não poluir console/admin.
  }

  return fromBrowser();
};

/** Preferência explícita do usuário (LanguageSelector). Sem isto, default = pt-BR. */
export const USER_LANG_PREF_KEY = 'upclinic_lang_user';

const readInitialLanguage = (): string => {
  try {
    if (typeof localStorage === 'undefined') return 'pt-BR';
    const userChose = localStorage.getItem(USER_LANG_PREF_KEY);
    const saved = localStorage.getItem('i18nextLng');
    if (userChose && saved && localeConfigs[saved as keyof typeof localeConfigs]) {
      return saved;
    }
  } catch {
    /* ignore */
  }
  return 'pt-BR';
};

// Configuração do i18next — pt-BR por padrão; EN/ES só se o usuário escolher
i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    resources: {
      'pt-BR': { translation: ptBR },
      'en-US': { translation: enUS },
      'es-ES': { translation: esES },
      'fr-FR': { translation: frFR },
      'de-DE': { translation: deDE },
      'it-IT': { translation: itIT },
      'zh-CN': { translation: zhCN },
      'ja-JP': { translation: jaJP },
      'ko-KR': { translation: koKR },
      'ru-RU': { translation: ruRU },
      'ar-SA': { translation: arSA },
    },

    lng: readInitialLanguage(),
    fallbackLng: 'pt-BR',
    debug: process.env.NODE_ENV === 'development',

    interpolation: {
      escapeValue: false,
    },

    backend: {
      loadPath: '/locales/{{lng}}.json',
    },
  });

// Inicialização automática de localização
export const initializeLocale = async () => {
  try {
    const userChose = localStorage.getItem(USER_LANG_PREF_KEY);
    const savedLocale = localStorage.getItem('i18nextLng');

    // Só respeita idioma salvo se o usuário escolheu explicitamente (EN/ES/etc.)
    if (userChose && savedLocale && localeConfigs[savedLocale as keyof typeof localeConfigs]) {
      await i18n.changeLanguage(savedLocale);
      applyLocaleConfig(savedLocale);
      return;
    }

    // Novos visitantes: sempre pt-BR (produto BR). IP/navegador não forçam EN.
    await i18n.changeLanguage('pt-BR');
    localStorage.setItem('i18nextLng', 'pt-BR');
    applyLocaleConfig('pt-BR');
  } catch (error) {
    console.error('Erro ao inicializar localização:', error);
    await i18n.changeLanguage('pt-BR');
    applyLocaleConfig('pt-BR');
  }
};

// Aplicar configurações regionais ao DOM
export const applyLocaleConfig = (locale: string) => {
  const config = localeConfigs[locale as keyof typeof localeConfigs];
  
  if (!config) return;

  // Aplicar direção de texto (RTL/LTR)
  document.documentElement.dir = config.rtl ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;

  // Aplicar atributos de localização
  document.documentElement.setAttribute('data-locale', locale);
  document.documentElement.setAttribute('data-currency', config.currency);
  document.documentElement.setAttribute('data-timezone', config.timezone);

  // Atualizar meta tags para SEO
  updateMetaTags(locale, config);
};

// Atualizar meta tags para SEO internacional
const updateMetaTags = (locale: string, config: any) => {
  // Language meta tag
  let langMeta = document.querySelector('meta[name="language"]');
  if (!langMeta) {
    langMeta = document.createElement('meta');
    langMeta.setAttribute('name', 'language');
    document.head.appendChild(langMeta);
  }
  langMeta.setAttribute('content', locale);

  // Currency meta tag
  let currencyMeta = document.querySelector('meta[name="currency"]');
  if (!currencyMeta) {
    currencyMeta = document.createElement('meta');
    currencyMeta.setAttribute('name', 'currency');
    document.head.appendChild(currencyMeta);
  }
  currencyMeta.setAttribute('content', config.currency);

  // Geographic meta tags
  let geoMeta = document.querySelector('meta[name="geo.region"]');
  if (!geoMeta) {
    geoMeta = document.createElement('meta');
    geoMeta.setAttribute('name', 'geo.region');
    document.head.appendChild(geoMeta);
  }
  
  // Mapear locale para região
  const regionMap: Record<string, string> = {
    'pt-BR': 'BR',
    'pt-PT': 'PT',
    'en-US': 'US',
    'en-GB': 'GB',
    'es-ES': 'ES',
    'es-MX': 'MX',
    'fr-FR': 'FR',
    'de-DE': 'DE',
    'it-IT': 'IT',
    'zh-CN': 'CN',
    'ja-JP': 'JP',
    'ko-KR': 'KR',
    'ru-RU': 'RU',
    'ar-SA': 'SA',
  };
  
  const region = regionMap[locale] || 'US';
  geoMeta.setAttribute('content', region);
};

// Funções utilitárias para formatação
export const formatCurrency = (amount: number, locale?: string): string => {
  const currentLocale = locale || i18n.language;
  const config = localeConfigs[currentLocale as keyof typeof localeConfigs];
  
  if (!config) return amount.toString();

  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: config.currency,
  }).format(amount);
};

export const formatDate = (date: Date, locale?: string): string => {
  const currentLocale = locale || i18n.language;
  const config = localeConfigs[currentLocale as keyof typeof localeConfigs];
  
  if (!config) return date.toLocaleDateString();

  return new Intl.DateTimeFormat(currentLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

export const formatTime = (date: Date, locale?: string): string => {
  const currentLocale = locale || i18n.language;
  const config = localeConfigs[currentLocale as keyof typeof localeConfigs];
  
  if (!config) return date.toLocaleTimeString();

  return new Intl.DateTimeFormat(currentLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: config.timeFormat.includes('a'),
  }).format(date);
};

export const formatNumber = (number: number, locale?: string): string => {
  const currentLocale = locale || i18n.language;
  
  return new Intl.NumberFormat(currentLocale).format(number);
};

// Hook para mudança de idioma (preferência explícita do usuário)
export const changeLanguage = async (locale: string) => {
  await i18n.changeLanguage(locale);
  applyLocaleConfig(locale);
  localStorage.setItem('i18nextLng', locale);
  localStorage.setItem(USER_LANG_PREF_KEY, '1');
  
  // Recarregar a página para aplicar todas as mudanças
  window.location.reload();
};

// Preços regionalizados com desconto de 65%
export const getRegionalPricing = (currency: string) => {
  const basePricing = {
    'BRL': {
      basic: { monthly: 35, annual: 350 },      // Era 99, agora 35
      professional: { monthly: 70, annual: 700 }, // Era 199, agora 70  
      enterprise: { monthly: 140, annual: 1400 }  // Era 399, agora 140
    },
    'USD': {
      basic: { monthly: 7, annual: 70 },         // Era 19, agora 7
      professional: { monthly: 14, annual: 140 }, // Era 39, agora 14
      enterprise: { monthly: 28, annual: 280 }    // Era 79, agora 28
    },
    'EUR': {
      basic: { monthly: 6, annual: 60 },         // Era 18, agora 6
      professional: { monthly: 13, annual: 130 }, // Era 36, agora 13
      enterprise: { monthly: 25, annual: 250 }    // Era 72, agora 25
    },
    'GBP': {
      basic: { monthly: 5, annual: 50 },         // Era 15, agora 5
      professional: { monthly: 11, annual: 110 }, // Era 32, agora 11
      enterprise: { monthly: 22, annual: 220 }    // Era 65, agora 22
    },
    'MXN': {
      basic: { monthly: 105, annual: 1050 },     // Era 299, agora 105
      professional: { monthly: 210, annual: 2100 }, // Era 599, agora 210
      enterprise: { monthly: 350, annual: 3500 }  // Era 999, agora 350
    },
    'CNY': {
      basic: { monthly: 50, annual: 500 },       // Era 138, agora 50
      professional: { monthly: 98, annual: 980 }, // Era 278, agora 98
      enterprise: { monthly: 175, annual: 1750 }  // Era 498, agora 175
    },
    'JPY': {
      basic: { monthly: 1050, annual: 10500 },   // Era 2999, agora 1050
      professional: { monthly: 2100, annual: 21000 }, // Era 5999, agora 2100
      enterprise: { monthly: 3500, annual: 35000 } // Era 9999, agora 3500
    },
    'KRW': {
      basic: { monthly: 9100, annual: 91000 },   // Era 25999, agora 9100
      professional: { monthly: 18200, annual: 182000 }, // Era 51999, agora 18200
      enterprise: { monthly: 31500, annual: 315000 } // Era 89999, agora 31500
    },
    'RUB': {
      basic: { monthly: 630, annual: 6300 },     // Era 1799, agora 630
      professional: { monthly: 1260, annual: 12600 }, // Era 3599, agora 1260
      enterprise: { monthly: 2100, annual: 21000 } // Era 5999, agora 2100
    },
    'SAR': {
      basic: { monthly: 26, annual: 260 },       // Era 75, agora 26
      professional: { monthly: 53, annual: 530 }, // Era 150, agora 53
      enterprise: { monthly: 88, annual: 880 }    // Era 249, agora 88
    }
  };

  return basePricing[currency as keyof typeof basePricing] || basePricing['USD'];
};

// Obter configuração atual
export const getCurrentLocaleConfig = () => {
  const currentLocale = i18n.language;
  return localeConfigs[currentLocale as keyof typeof localeConfigs] || localeConfigs['en-US'];
};

// Lista de idiomas disponíveis
export const getAvailableLanguages = () => {
  return Object.entries(localeConfigs).map(([code, config]) => ({
    code,
    name: config.name,
    flag: config.flag,
  }));
};

export default i18n; 